import { TreeNode } from '@shared/ui-native/Tree';
import * as olExtent from 'ol/extent';
import { IModelInfo } from '@shared/models/types';
import { transform as olTransform } from 'ol/proj';
import { Auth, IAuthToken } from '@shared/Auth';
import Static from 'ol/source/ImageStatic';
import ImageLayer from 'ol/layer/Image';
import { StructureLayer } from './StructureLayer';
import LayerGroup from 'ol/layer/Group';
import BaseLayer from 'ol/layer/Base';
import z from 'zod';

const ImageResponseSchema = z
    .object({
        extent: z.tuple([z.number(), z.number(), z.number(), z.number()]).describe('The bounding box of the image in the format [minX, minY, maxX, maxY]'),
        url: z.string().optional().describe('The download URL for the image'),
        dataUrl: z.string().optional(),
    })
    .transform(data => ({
        extent: data.extent,
        url: data.url ?? data.dataUrl,
    }));

const TiledImageResponseSchema = z.array(ImageResponseSchema);

type ImageResponse = z.infer<typeof ImageResponseSchema>;
type TiledImageResponse = z.infer<typeof TiledImageResponseSchema>;

type ModelStatus = {
    available: boolean;
    simplifiedAvailable: boolean;
    meshImageAvailable: boolean;
    tiledImageAvailable: boolean;
    outlinesAvailable: boolean;
    processingStarted: string | null;
    processingCompleted: string | null;
};

// New: same origin, proxied through kuura — single uuid
const MODEL_API_BASE = '/kuura/v1/viz/model/';

// Old: direct microservice — project + file uuid
// const MODEL_API_BASE = `${kuura.AwsApiGatewayDomain()}/viz/v1/`;

const modelUrl = (uuid: string, endpoint: string): string =>
    `${MODEL_API_BASE}${uuid}/${endpoint}`;

// Old microservice variant (uncomment with old MODEL_API_BASE):
// const modelUrl = (projectUuid: string, fileUuid: string, endpoint: string): string =>
//     `${MODEL_API_BASE}${projectUuid}/${fileUuid}/${endpoint}`;

const fetchModelStatus = async (uuid: string): Promise<ModelStatus | null> => {
    try {
        const headers = await Auth.getHeaders();
        const response = await fetch(modelUrl(uuid, 'status'), {
            headers
        });
        if (!response.ok) return null;
        return await response.json();
    } catch (e) {
        console.warn('Could not fetch model status for', uuid);
        return null;
    }
};

const pollModelUntilReady = (uuid: string): Promise<ModelStatus | null> => {
    return new Promise(resolve => {
        const interval = setInterval(async () => {
            const status = await fetchModelStatus(uuid);
            if (!status || status.processingCompleted) {
                clearInterval(interval);
                resolve(status);
            }
        }, 20_000);
    });
};

const projection = 'EPSG:3857';

export class RasterModel {
    uuid: string;
    node: TreeNode;
    extent: olExtent.Extent;

    meshImageLayer: ImageLayer<Static>;
    tiledImageLayer: LayerGroup;

    structureLayer: StructureLayer;

    modelInfo: IModelInfo;

    originalNodeText: string;

    constructor(node: TreeNode, structureLayer: StructureLayer) {
        this.uuid = node.uuid;
        this.node = node;
        this.structureLayer = structureLayer;
        this.extent = olExtent.createEmpty();
        this.originalNodeText = node.text;
    }

    public async load() {
        let status = await fetchModelStatus(this.uuid);
        if (!status) return;

        if (status.processingStarted && !status.processingCompleted) {
            status = await pollModelUntilReady(this.uuid);
            if (!status) return;
        }

        if (status.meshImageAvailable) {
            await this.loadMeshImage();
            if (status.tiledImageAvailable) {
                await this.loadTiledImage();
            }
        }
    }

    public dispose() {
        if (this.meshImageLayer) {
            this.meshImageLayer.setSource(null);
            this.structureLayer.removeLayer(this.meshImageLayer);
            this.meshImageLayer = null;
        }
        if (this.tiledImageLayer) {
            this.tiledImageLayer.getLayers().forEach((layer: BaseLayer) => {
                (layer as ImageLayer<Static>).setSource(null);
            });
            this.structureLayer.removeLayer(this.tiledImageLayer);
            this.tiledImageLayer = null;
        }
    }

    private async loadMeshImage(maxZoom = 16) {
        const url = modelUrl(this.uuid, 'mesh-image');

        const token = await Auth.getToken();
        const resp: Response = await fetch(url, {
            headers: { Authorization: 'Bearer ' + token.accessToken },
        });

        const data: unknown = await resp.json();
        const parsedData: z.ZodSafeParseResult<ImageResponse> = ImageResponseSchema.safeParse(data);

        if (!parsedData.success) {
            console.error('Invalid mesh image response', parsedData.error);
            this.node.setError(__('js_map.invalid_coordinates'));
            this.node.setChecked(0, true, false);
            return;
        }

        const imageResponse: ImageResponse = parsedData.data;

        const minCoord = [imageResponse.extent[0], imageResponse.extent[1]];
        const maxCoord = [imageResponse.extent[2], imageResponse.extent[3]];
        const min = olTransform(minCoord, 'EPSG:4326', 'EPSG:3857');
        const max = olTransform(maxCoord, 'EPSG:4326', 'EPSG:3857');

        const transformedExtent: olExtent.Extent = olExtent.createEmpty();
        olExtent.extendCoordinates(transformedExtent, [min, max]);

        this.extent = transformedExtent;

        const source = new Static({
            url: imageResponse.url,
            projection: projection,
            imageExtent: transformedExtent,
            crossOrigin: 'anonymous',
        });

        this.meshImageLayer = new ImageLayer({
            source: source,
            zIndex: 10,
            maxZoom: maxZoom,
        });

        this.structureLayer.addLayer(this.meshImageLayer);
    }

    private async loadTiledImage() {
        const url = modelUrl(this.uuid, 'tiled-mesh-image');

        const token: IAuthToken = await Auth.getToken();
        const resp: Response = await fetch(url, {
            headers: { Authorization: 'Bearer ' + token.accessToken },
        });

        const response: unknown = await resp.json();

        const parsedData: z.ZodSafeParseResult<TiledImageResponse> = TiledImageResponseSchema.safeParse(response);

        if (!parsedData.success) {
            console.error('Invalid tiled mesh image response', parsedData.error);
            this.node.setError(__('js_map.invalid_coordinates'));
            return;
        }

        const tiledImages: TiledImageResponse = parsedData.data;

        this.tiledImageLayer = new LayerGroup();

        for (const tiledImage of tiledImages) {
            const minCoord = [tiledImage.extent[0], tiledImage.extent[1]];
            const maxCoord = [tiledImage.extent[2], tiledImage.extent[3]];
            const min = olTransform(minCoord, 'EPSG:4326', 'EPSG:3857');
            const max = olTransform(maxCoord, 'EPSG:4326', 'EPSG:3857');

            const transformedExtent: olExtent.Extent = olExtent.createEmpty();
            olExtent.extendCoordinates(transformedExtent, [min, max]);

            const source = new Static({
                url: tiledImage.url,
                projection: projection,
                imageExtent: transformedExtent,
                crossOrigin: 'anonymous',
            });

            const layer = new ImageLayer({
                source: source,
                zIndex: 10,
                minZoom: 16,
            });

            this.tiledImageLayer.getLayers().push(layer);
        }

        this.structureLayer.addLayer(this.tiledImageLayer);
    }

    setOpacity(solid: number, line: number) {
    }
}
